/**
 * Module Spec Type Definitions
 *
 * These types define the contract for module specifications in botbat-console.
 * Each module must provide a spec file conforming to the ModuleSpec interface.
 */

import { type LucideIcon } from "lucide-react";
import { type AppAbility } from "@/stores/casl.store";

// ============================================================================
// Permission Types
// ============================================================================

/**
 * CASL permission actions supported by the application.
 */
export type PermissionAction =
  | "manage"
  | "create"
  | "read"
  | "update"
  | "delete"
  | "execute"
  | "publish"
  | "configure"
  | "import"
  | "export";

/**
 * CASL permission subjects (resources) in the application.
 */
export type PermissionSubject =
  | "all"
  | "organization"
  | "workflow"
  | "campaign"
  | "channel"
  | "cdp"
  | "integration"
  | "conversation"
  | "user"
  | "role"
  | "analytics"
  | "billing"
  | "apiKey"
  | "webhook"
  | "whatsapp-onboarding"
  | "messenger-onboarding";

/**
 * Permission configuration for module access control.
 */
export interface ModulePermission {
  action: PermissionAction;
  subject: PermissionSubject;
}

// ============================================================================
// Navigation Types
// ============================================================================

/**
 * Navigation group identifiers matching sidebar sections.
 * Matches console-v2 sidebar structure.
 */
export type NavGroupId =
  | "connect"
  | "overview"
  | "build"
  | "run"
  | "observe"
  | "team"
  | "customers"
  | "account";

/**
 * Module development/release status.
 */
export type ModuleStatus = "active" | "in-development" | "coming-soon";

/**
 * Navigation configuration for sidebar and routing.
 */
export interface ModuleNavigation {
  /** Route path for this module (must start with '/') */
  path: string;
  /** Parent module ID for nested navigation */
  parent?: string;
  /** Navigation group this module belongs to */
  group: NavGroupId;
  /** Sort order within the navigation group (lower = higher) */
  order: number;
  /** Whether this is a top-level nav item */
  isTopLevel?: boolean;
  /** Subgroup within the nav group for nested collapsible sections */
  subgroup?: string;
}

/**
 * Internationalization configuration for module labels.
 */
export interface ModuleI18n {
  /** i18n namespace for module translations */
  namespace: string;
  /** Translation key for module title */
  titleKey?: string;
  /** Translation key for navigation label */
  navLabelKey?: string;
  /** Translation key for module description */
  descriptionKey?: string;
}

/**
 * Module metadata for UI rendering and feature control.
 */
export interface ModuleMeta {
  /** Development status of the module */
  status: ModuleStatus;
  /** Feature flag name controlling module visibility */
  featureFlag?: string;
  /** Static description for tooltips */
  description?: string;
}

// ============================================================================
// Module Spec
// ============================================================================

/**
 * Complete module specification.
 */
export interface ModuleSpec {
  /** Unique module identifier (kebab-case) */
  id: string;
  /** Lucide icon component for this module */
  icon: LucideIcon;
  /** Internationalization configuration */
  i18n: ModuleI18n;
  /** Navigation configuration (optional for non-navigable modules) */
  nav?: ModuleNavigation;
  /** Permission requirements (optional for public modules) */
  permission?: ModulePermission;
  /** Module metadata */
  meta: ModuleMeta;
}

// ============================================================================
// Navigation Item Types (Derived)
// ============================================================================

/**
 * Rendered navigation item derived from ModuleSpec.
 */
export interface NavItem {
  /** Module ID */
  id: string;
  /** Navigation path */
  to: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Translated label */
  label: string;
  /** Development status for badge rendering */
  status: ModuleStatus;
  /** Translated description for tooltip */
  description?: string;
  /** Whether this item is disabled */
  disabled: boolean;
  /** Whether this item has a notification indicator */
  hasNotification?: boolean;
  /** Nested navigation items */
  children?: NavItem[];
}

/**
 * Subgroup within a navigation group for collapsible nested sections.
 */
export interface NavSubgroup {
  /** Subgroup identifier */
  id: string;
  /** Translated subgroup title */
  title: string;
  /** Navigation items in this subgroup */
  items: NavItem[];
}

/**
 * Navigation group for sidebar sections.
 */
export interface NavGroup {
  /** Group identifier */
  id: NavGroupId;
  /** Translated group title */
  title: string;
  /** Top-level navigation items in this group (items without a subgroup) */
  items: NavItem[];
  /** Subgroups for collapsible nested sections */
  subgroups?: NavSubgroup[];
}

// ============================================================================
// Breadcrumb Types
// ============================================================================

/**
 * Single breadcrumb segment.
 */
export interface Breadcrumb {
  /** Translated label */
  label: string;
  /** Navigation path (undefined for current page) */
  path?: string;
  /** Module ID if this corresponds to a registered module */
  moduleId?: string;
  /** Optional icon */
  icon?: LucideIcon;
}

/**
 * Complete breadcrumb trail from Home to current page.
 */
export type BreadcrumbTrail = Breadcrumb[];

// ============================================================================
// Context Types
// ============================================================================

/**
 * User context for permission-based filtering.
 */
export interface UserContext {
  /** CASL ability instance */
  ability: AppAbility;
  /** Whether permissions are still loading */
  isLoading: boolean;
}

/**
 * Feature flag context for flag-based filtering.
 */
export interface FeatureFlagContext {
  /** Check if a feature flag is enabled */
  isEnabled: (flagName: string) => boolean;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Navigation group i18n keys.
 * Matches console-v2 sidebar structure.
 */
export const NAV_GROUP_I18N_KEYS: Record<NavGroupId, string> = {
  connect: "modules:nav.groups.connect",
  overview: "modules:nav.groups.overview",
  build: "modules:nav.groups.build",
  run: "modules:nav.groups.run",
  observe: "modules:nav.groups.observe",
  team: "modules:nav.groups.team",
  customers: "modules:nav.groups.customers",
  account: "modules:nav.groups.account",
};

/**
 * Default navigation group order.
 * Matches console-v2 sidebar structure.
 */
export const NAV_GROUP_ORDER: NavGroupId[] = [
  "overview",
  "connect",
  "build",
  "run",
  "observe",
  "team",
  "customers",
  "account",
];

/**
 * Subgroup definitions for the customers nav group.
 * Defines display order and i18n keys for each subgroup.
 */
export const CUSTOMER_SUBGROUPS: {
  id: string;
  i18nKey: string;
  order: number;
}[] = [
    { id: "cdp", i18nKey: "modules:nav.subgroups.cdp", order: 0 },
    { id: "structure", i18nKey: "modules:nav.subgroups.structure", order: 1 },
    { id: "dynamics", i18nKey: "modules:nav.subgroups.dynamics", order: 2 },
    {
      id: "configuration",
      i18nKey: "modules:nav.subgroups.configuration",
      order: 3,
    },
  ];

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if an object is a valid ModuleSpec.
 */
export function isModuleSpec(obj: unknown): obj is ModuleSpec {
  if (!obj || typeof obj !== "object") return false;
  const spec = obj as Record<string, unknown>;

  // Lucide icons are ForwardRefExoticComponent (objects with $$typeof), not plain functions
  const hasValidIcon =
    typeof spec.icon === "function" ||
    (typeof spec.icon === "object" &&
      spec.icon !== null &&
      "$$typeof" in (spec.icon as object));

  return (
    typeof spec.id === "string" &&
    hasValidIcon &&
    typeof spec.i18n === "object" &&
    typeof spec.meta === "object"
  );
}
